import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import {
  HorizontalContentCard,
  HorizontalContentCards,
} from '@/components/redesign/HorizontalContentCards';

type RedesignSlice = Extract<
  Content.CaseIndustryShowcaseSlice,
  { variation: 'redesignLogoCards' }
>;

export type CaseIndustryShowcaseRedesignProps =
  SliceComponentProps<RedesignSlice>;

const CaseIndustryShowcaseRedesign = ({
  slice,
}: CaseIndustryShowcaseRedesignProps): JSX.Element => {
  const { headline, subheadline, background_image, industries } = slice.primary;

  return (
    <HorizontalContentCards
      sliceType={slice.slice_type}
      variation={slice.variation}
      backgroundImage={background_image}
      headline={headline}
      subheadline={subheadline}
      gridClassName="grid-cols-1 items-stretch gap-4 lg:grid-cols-2"
    >
      {industries.map((item, index) => (
        <HorizontalContentCard key={index} className="h-full">
          {item.badge_label && (
            <div className="flex items-center border-l border-[#4b0fea] py-1 pl-3">
              <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#041118]">
                {item.badge_label}
              </span>
            </div>
          )}

          {isFilled.image(item.brand_logo) ? (
            <PrismicNextImage
              field={item.brand_logo}
              className="h-8 w-auto max-w-[220px] self-start object-contain"
              fallbackAlt=""
            />
          ) : (
            item.brand_name && (
              <p className="text-[27px] font-bold leading-[1.2] text-[#041118]">
                {item.brand_name}
              </p>
            )
          )}

          {item.stat_headline && (
            <p className="text-[20px] font-semibold leading-[1.4] text-[#041118] md:text-[24px]">
              {item.stat_headline}
            </p>
          )}

          {isFilled.richText(item.description) && (
            <div className="text-[16px] font-light leading-[1.5] text-[#041118] [&_p]:m-0 [&_p+p]:mt-2 md:text-[18px]">
              <PrismicRichText field={item.description} />
            </div>
          )}

          {isFilled.link(item.cta_link) && (
            <div className="mt-auto">
              <OutlineCtaLink
                field={item.cta_link}
                label={item.cta_label || 'View case study'}
                tone="light"
                stripTrailingArrow
                className="whitespace-nowrap"
              />
            </div>
          )}
        </HorizontalContentCard>
      ))}
    </HorizontalContentCards>
  );
};

export default CaseIndustryShowcaseRedesign;
